import { useEffect, useRef, useState } from 'react';
import { Copy, Download, File as FileIcon, Folder, FolderPlus, Image as ImageIcon, Pencil, Trash2, Upload, CornerLeftUp } from 'lucide-react';
import {
  Button, Input, PageShell, SectionCard, Skeleton, DataTable, type DataTableColumn,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose,
  toast,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { ApiClientError } from '@/lib/http-core';
import {
  WEB_FTP_EXTENSIONS,
  WEB_FTP_MAX_BYTES,
  fetchWebFtpDownloadUrl,
  fetchWebFtpPage,
  isValidWebFtpName,
  useWebFtpListQuery,
  useWebFtpMutations,
  webFtpExtensionOk,
  type WebFtpEntry,
} from '../hooks/useWebFtp';

/** 고도몰 Web FTP 안내 문구 그대로. */
const NAME_RULE_MESSAGE = '파일명은 영문으로 작성해 주세요. (영문·숫자·. _ - 만, 첫 글자는 영문 또는 숫자, 100자 이하)';
const MAX_FILES = 50;

/** 미리보기로 보여 줄 이미지 형식(업로드 허용 형식 중 이미지). */
const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'ico'];
export const isImageName = (name: string): boolean => IMAGE_EXTENSIONS.includes(name.split('.').pop()?.toLowerCase() ?? '');

const formatBytes = (n: number | null): string => {
  if (n == null) return '-';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const KST = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
});
const formatKst = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const p = (t: Intl.DateTimeFormatPartTypes) => KST.formatToParts(d).find((x) => x.type === t)?.value ?? '';
  return `${p('year')}-${p('month')}-${p('day')} ${p('hour')}:${p('minute')}`;
};

const errorText = (e: unknown, fallback: string): string =>
  // BE error.message 에 사용자에게 보여 줄 문구가 들어 있다.
  e instanceof ApiClientError && e.message ? e.message : friendlyMessage(e, fallback);

const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

type UpRow = { type: 'UP'; name: '..'; path: string };
type Row = WebFtpEntry | UpRow;

/**
 * QA-227 — Web FTP. 상품 상세를 HTML 코드로 넣을 때 쓸 이미지를 폴더별로 올리고 주소를 복사한다(고도몰 Web FTP 대체).
 * BE 가 정한 최상위(운영 files/, QA files-qa/ — catalog everline.web-ftp.root-prefix) 아래만 보인다. 이관 data/... 는 보이지 않는다.
 * 주소는 BE 응답(url)을 그대로 쓴다 — 환경마다 경로가 달라도 BO 는 바꿀 것이 없다.
 */
export const WebFtpPage = () => {
  const [path, setPath] = useState('');
  const [keywordDraft, setKeywordDraft] = useState('');
  const [keyword, setKeyword] = useState('');
  const query = useWebFtpListQuery(path, keyword);
  const { createFolder, upload, rename, remove } = useWebFtpMutations();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = useState(false);

  const [folderOpen, setFolderOpen] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [renameTarget, setRenameTarget] = useState<WebFtpEntry | null>(null);
  const [newName, setNewName] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<WebFtpEntry | null>(null);
  const [overwriteFiles, setOverwriteFiles] = useState<File[] | null>(null);
  const [preview, setPreview] = useState<WebFtpEntry | null>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const listing = query.data;

  // catalog #63 — [더 보기]로 이어 붙인 묶음. 첫 묶음이 새로 오면(폴더 이동·검색·업로드·이름 변경·삭제 후 재조회) 비운다 —
  // 이전 cursor 는 그 시점 목록 기준이라 계속 쓰면 항목이 빠지거나 겹친다.
  const [more, setMore] = useState<{ items: WebFtpEntry[]; nextCursor: string | null }>({ items: [], nextCursor: null });
  const [loadingMore, setLoadingMore] = useState(false);
  useEffect(() => {
    setMore({ items: [], nextCursor: null });
  }, [listing, path, keyword]);
  const nextCursor = more.items.length > 0 ? more.nextCursor : (listing?.nextCursor ?? null);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await fetchWebFtpPage(path, keyword, nextCursor);
      setMore((prev) => ({ items: [...prev.items, ...page.items], nextCursor: page.nextCursor ?? null }));
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 400) {
        // 잘못된·만료된 cursor — 처음부터 다시.
        toast.error(errorText(e, '목록을 처음부터 다시 불러옵니다.'));
        setMore({ items: [], nextCursor: null });
        void query.refetch();
      } else {
        toast.error(errorText(e, '목록을 더 불러오지 못했습니다.'));
      }
    } finally {
      setLoadingMore(false);
    }
  };
  const breadcrumbs = listing?.breadcrumbs ?? [{ name: 'Home', path: '' }];
  const parentPath = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2]!.path : null;

  const go = (p: string) => {
    setPath(p);
    setKeyword('');
    setKeywordDraft('');
  };

  // 업로드 — 형식·크기·이름을 먼저 거른 뒤 올린다. 같은 이름(409)이면 덮어쓰기를 묻는다.
  const startUpload = async (picked: File[], overwrite = false) => {
    if (picked.length === 0) return;
    if (picked.length > MAX_FILES) {
      toast.error(`한 번에 ${MAX_FILES}개까지 올릴 수 있습니다.`);
      return;
    }
    const badName = picked.find((f) => !isValidWebFtpName(f.name));
    if (badName) {
      toast.error(`${badName.name}: ${NAME_RULE_MESSAGE}`);
      return;
    }
    const badType = picked.find((f) => !webFtpExtensionOk(f.name));
    if (badType) {
      toast.error(`${badType.name}: 올릴 수 없는 형식입니다. (${WEB_FTP_EXTENSIONS.join(', ')})`);
      return;
    }
    const tooBig = picked.find((f) => f.size > WEB_FTP_MAX_BYTES);
    if (tooBig) {
      toast.error(`${tooBig.name}: 파일 하나에 20MB 까지 올릴 수 있습니다.`);
      return;
    }
    try {
      await upload.mutateAsync({ path, files: picked, overwrite });
      toast.success(`${picked.length}개 파일을 올렸습니다.`);
    } catch (e) {
      if (!overwrite && e instanceof ApiClientError && e.status === 409) {
        setOverwriteFiles(picked);
        return;
      }
      toast.error(errorText(e, '파일을 올리지 못했습니다.'));
    }
  };

  const copyUrl = async (url: string) => {
    if (await copyText(url)) toast.success('주소를 복사했습니다.');
    else toast.error(`복사하지 못했습니다. 직접 복사해 주세요: ${url}`);
  };

  const download = async (entry: WebFtpEntry) => {
    try {
      const url = await fetchWebFtpDownloadUrl(entry.path);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      toast.error(errorText(e, '다운로드 주소를 받지 못했습니다.'));
    }
  };

  const submitFolder = async () => {
    const name = folderName.trim();
    if (!isValidWebFtpName(name)) {
      setDialogError(NAME_RULE_MESSAGE);
      return;
    }
    try {
      await createFolder.mutateAsync({ path, name });
      setFolderOpen(false);
      toast.success('폴더를 만들었습니다.');
    } catch (e) {
      setDialogError(errorText(e, '폴더를 만들지 못했습니다.'));
    }
  };

  const submitRename = async () => {
    if (!renameTarget) return;
    const name = newName.trim();
    if (!isValidWebFtpName(name)) {
      setDialogError(NAME_RULE_MESSAGE);
      return;
    }
    try {
      await rename.mutateAsync({ path, name: renameTarget.name, newName: name });
      setRenameTarget(null);
      toast.success('이름을 바꿨습니다.');
    } catch (e) {
      setDialogError(errorText(e, '이름을 바꾸지 못했습니다.'));
    }
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove.mutateAsync({ path, name: deleteTarget.name });
      setDeleteTarget(null);
      toast.success('삭제했습니다.');
    } catch (e) {
      setDialogError(errorText(e, '삭제하지 못했습니다.'));
    }
  };

  const rows: Row[] = [
    ...(parentPath !== null && !keyword ? [{ type: 'UP', name: '..', path: parentPath } as UpRow] : []),
    ...(listing?.items ?? []),
    ...more.items,
  ];

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'name',
      header: '이름',
      headerAlign: 'center',
      cell: (r) =>
        r.type === 'FILE' && isImageName(r.name) ? (
          // 이미지는 이미지 아이콘 + 이름을 누르면 미리보기 팝업.
          <button
            type="button"
            className="flex min-w-0 items-center gap-2 text-left text-sm hover:underline"
            onClick={() => setPreview(r)}
            title={r.name}
            data-testid="webftp-image"
          >
            <ImageIcon size={14} aria-hidden="true" style={{ color: 'var(--chart-primary, var(--accent-default))' }} />
            <span className="truncate">{r.name}</span>
          </button>
        ) : r.type === 'FILE' ? (
          <span className="flex min-w-0 items-center gap-2 text-sm" title={r.name}>
            <FileIcon size={14} aria-hidden="true" style={{ color: 'var(--content-muted)' }} />
            <span className="truncate">{r.name}</span>
          </span>
        ) : (
          <button
            type="button"
            className="flex min-w-0 items-center gap-2 text-left text-sm font-medium hover:underline"
            onClick={() => go(r.path)}
            data-testid={r.type === 'UP' ? 'webftp-up' : 'webftp-folder'}
            title={r.type === 'UP' ? '상위 폴더' : r.name}
          >
            {r.type === 'UP' ? (
              <CornerLeftUp size={14} aria-hidden="true" />
            ) : (
              <Folder size={14} aria-hidden="true" style={{ color: 'var(--accent-default)' }} />
            )}
            <span className="truncate">{r.name}</span>
          </button>
        ),
    },
    {
      key: 'size',
      header: '크기',
      align: 'right',
      width: 100,
      cell: (r) => <span className="text-xs tabular-nums">{r.type === 'FILE' ? formatBytes(r.sizeBytes) : '-'}</span>,
    },
    {
      key: 'lastModified',
      header: '최종수정일',
      align: 'center',
      width: 150,
      cell: (r) => <span className="text-xs tabular-nums">{r.type === 'FILE' ? formatKst(r.lastModified) : '-'}</span>,
    },
    {
      key: 'actions',
      header: '처리',
      align: 'center',
      width: 400,
      // 아이콘·이름은 그대로 두고 한 줄로(줄바꿈 없이).
      cell: (r) =>
        r.type === 'UP' ? null : (
          <span className="flex flex-nowrap items-center justify-center gap-1 whitespace-nowrap">
            <Button variant="outline" size="sm" onClick={() => void copyUrl(r.url)} data-testid="webftp-copy">
              <Copy size={12} aria-hidden="true" />
              주소복사
            </Button>
            {r.type === 'FILE' ? (
              <Button variant="outline" size="sm" onClick={() => void download(r)} data-testid="webftp-download">
                <Download size={12} aria-hidden="true" />
                다운로드
              </Button>
            ) : null}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setRenameTarget(r);
                setNewName(r.name);
                setDialogError(null);
              }}
              data-testid="webftp-rename"
            >
              <Pencil size={12} aria-hidden="true" />
              이름변경
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDeleteTarget(r);
                setDialogError(null);
              }}
              data-testid="webftp-delete"
              style={{ color: 'var(--signal-danger)' }}
            >
              <Trash2 size={12} aria-hidden="true" />
              삭제
            </Button>
          </span>
        ),
    },
  ];

  return (
    <PageShell
      title="Web FTP"
      description="상품 상세 HTML 에 넣을 이미지를 폴더별로 올리고 주소를 복사합니다. QA 와 운영은 저장 위치가 다릅니다 — QA 에서 올린 파일은 운영 상품에 쓰지 마세요."
      data-testid="webftp-page"
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setFolderName('');
            setDialogError(null);
            setFolderOpen(true);
          }}
          data-testid="webftp-new-folder"
        >
          <FolderPlus size={14} aria-hidden="true" />
          새 폴더
        </Button>
      }
    >
      <SectionCard
        title={
          <nav aria-label="경로" className="flex flex-wrap items-center gap-1 text-sm" data-testid="webftp-breadcrumbs">
            {breadcrumbs.map((b, i) => (
              <span key={b.path} className="flex items-center gap-1">
                {i > 0 ? <span style={{ color: 'var(--content-muted)' }}>/</span> : null}
                {i === breadcrumbs.length - 1 ? (
                  <span className="font-semibold">{b.name}</span>
                ) : (
                  <button type="button" className="hover:underline" onClick={() => go(b.path)}>
                    {b.name}
                  </button>
                )}
              </span>
            ))}
          </nav>
        }
        aside={
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setKeyword(keywordDraft.trim());
            }}
          >
            <Input
              aria-label="이름 검색"
              placeholder="현재 폴더에서 이름 검색"
              value={keywordDraft}
              onChange={(e) => setKeywordDraft(e.target.value)}
              data-testid="webftp-keyword"
            />
            <Button type="submit" size="sm" data-testid="webftp-search">
              검색
            </Button>
          </form>
        }
      >
        <div className="space-y-3">
          <label
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              void startUpload(Array.from(e.dataTransfer.files ?? []));
            }}
            className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed px-4 py-6 text-sm"
            style={{
              borderColor: dragging ? 'var(--content-strong)' : 'var(--divider-strong)',
              background: dragging ? 'var(--surface-1)' : 'var(--surface-raised)',
              color: 'var(--content-muted)',
            }}
            data-testid="webftp-dropzone"
          >
            <input
              ref={fileInput}
              type="file"
              multiple
              className="sr-only"
              onChange={(e) => {
                void startUpload(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
              data-testid="webftp-file-input"
            />
            <span className="flex items-center gap-2 font-medium" style={{ color: 'var(--content-strong)' }}>
              <Upload size={14} aria-hidden="true" />
              {upload.isPending ? '올리는 중…' : '파일을 끌어다 놓거나 클릭해서 선택 (여러 개 가능)'}
            </span>
            <span className="text-xs">
              이미지(jpg·png·gif·webp·bmp·svg·ico) · html·css·js · 웹폰트 · 파일당 20MB · 한 번에 {MAX_FILES}개 · 파일명은 영문
            </span>
          </label>

          {query.isPending ? (
            <Skeleton className="h-40 w-full" />
          ) : query.isError ? (
            <div role="alert" className="flex items-center gap-2 text-sm" style={{ color: 'var(--signal-danger)' }}>
              {errorText(query.error, '목록을 불러오지 못했습니다.')}
              <Button variant="outline" size="sm" onClick={() => query.refetch()}>
                다시 시도
              </Button>
            </div>
          ) : (
            <>
              <DataTable
                columns={columns}
                rows={rows}
                rowKey={(r) => `${r.type}:${r.path}`}
                emptyState={keyword ? '검색 결과가 없습니다.' : '비어 있는 폴더입니다.'}
              />
              {nextCursor ? (
                <div className="flex justify-center">
                  <Button variant="outline" size="sm" disabled={loadingMore} onClick={() => void loadMore()} data-testid="webftp-load-more">
                    {loadingMore ? '불러오는 중…' : '더 보기'}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </SectionCard>

      {/* 이미지 미리보기 */}
      <Dialog open={preview !== null} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-4xl" data-testid="webftp-preview">
          <DialogHeader>
            <DialogTitle className="truncate">{preview?.name}</DialogTitle>
            <DialogDescription>
              {preview ? `${formatBytes(preview.sizeBytes)} · ${formatKst(preview.lastModified)}` : ''}
            </DialogDescription>
          </DialogHeader>
          {preview ? (
            <div className="flex max-h-[65vh] items-center justify-center overflow-auto rounded-md" style={{ background: 'var(--surface-1)' }}>
              <img src={preview.url} alt={preview.name} className="max-h-[65vh] max-w-full object-contain" data-testid="webftp-preview-img" />
            </div>
          ) : null}
          <DialogFooter>
            {preview ? (
              <Button variant="outline" size="sm" onClick={() => void copyUrl(preview.url)}>
                <Copy size={12} aria-hidden="true" />
                주소복사
              </Button>
            ) : null}
            <DialogClose asChild>
              <Button variant="secondary" size="sm">닫기</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 새 폴더 */}
      <Dialog open={folderOpen} onOpenChange={setFolderOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>새 폴더</DialogTitle>
            <DialogDescription>{NAME_RULE_MESSAGE}</DialogDescription>
          </DialogHeader>
          <Input value={folderName} onChange={(e) => setFolderName(e.target.value)} aria-label="폴더 이름" data-testid="webftp-folder-name" />
          {dialogError ? <p role="alert" className="text-sm" style={{ color: 'var(--signal-danger)' }}>{dialogError}</p> : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">취소</Button>
            </DialogClose>
            <Button size="sm" disabled={createFolder.isPending} onClick={() => void submitFolder()} data-testid="webftp-folder-submit">
              만들기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 이름 변경 */}
      <Dialog open={renameTarget !== null} onOpenChange={(o) => { if (!o) setRenameTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>이름 변경</DialogTitle>
            <DialogDescription>
              이름을 바꾸면 주소도 바뀝니다. 이미 상품 상세에 넣은 주소는 이미지가 깨집니다. {NAME_RULE_MESSAGE}
            </DialogDescription>
          </DialogHeader>
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} aria-label="새 이름" data-testid="webftp-new-name" />
          {dialogError ? <p role="alert" className="text-sm" style={{ color: 'var(--signal-danger)' }}>{dialogError}</p> : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">취소</Button>
            </DialogClose>
            <Button size="sm" disabled={rename.isPending} onClick={() => void submitRename()} data-testid="webftp-rename-submit">
              바꾸기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 삭제 */}
      <Dialog open={deleteTarget !== null} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>삭제</DialogTitle>
            <DialogDescription>
              "{deleteTarget?.name}" 을(를) 삭제하시겠습니까? 이 주소를 쓰던 상품 상세의 이미지는 깨집니다.
              {deleteTarget?.type === 'FOLDER' ? ' 폴더는 비어 있을 때만 삭제됩니다.' : ''}
            </DialogDescription>
          </DialogHeader>
          {dialogError ? <p role="alert" className="text-sm" style={{ color: 'var(--signal-danger)' }}>{dialogError}</p> : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">취소</Button>
            </DialogClose>
            <Button variant="danger" size="sm" disabled={remove.isPending} onClick={() => void submitDelete()} data-testid="webftp-delete-submit">
              삭제
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 덮어쓰기 */}
      <Dialog open={overwriteFiles !== null} onOpenChange={(o) => { if (!o) setOverwriteFiles(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>덮어쓰시겠습니까?</DialogTitle>
            <DialogDescription>같은 이름의 파일이 이미 있습니다. 덮어쓰면 기존 파일이 바뀝니다(주소는 그대로).</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">취소</Button>
            </DialogClose>
            <Button
              size="sm"
              onClick={() => {
                const files = overwriteFiles ?? [];
                setOverwriteFiles(null);
                void startUpload(files, true);
              }}
              data-testid="webftp-overwrite-confirm"
            >
              덮어쓰기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
